import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React from 'react';
import { DataRoomDocument } from '../../shared/models';

interface DocumentActionButtonProps {
	isEmployee: boolean;
	isBanker: boolean;
	document: DataRoomDocument;
	editDocument: (document: DataRoomDocument) => void;
	moveDocument: (document: DataRoomDocument) => void;
	copyTo: (document: DataRoomDocument) => void;
	downloadHistory: (document: DataRoomDocument) => void;
	deleteDocument: (document: DataRoomDocument) => void;
	sendDocument: (document: DataRoomDocument) => void;
	linkDocument: (document: DataRoomDocument) => void;
}

type DocumentActionItem = keyof typeof DocumentActionItemMap;
const DocumentActionItemMap = {
	Edit: 'Edit',
	Move: 'Move',
	CopyTo: 'Copy To',
	DownloadHistory: 'Download History',
	EmailLender: 'Email Lender',
	Delete: 'Delete',
	Link: 'Link to Diligence Request',
};

export const DocumentActionButton: React.FC<DocumentActionButtonProps> = ({
	isEmployee,
	isBanker,
	document,
	editDocument,
	moveDocument,
	copyTo,
	downloadHistory,
	deleteDocument,
	sendDocument,
	linkDocument,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: DocumentActionItem) => {
		switch (action) {
			case 'Edit':
				editDocument(document);
				break;
			case 'Move':
				moveDocument(document);
				break;
			case 'CopyTo':
				copyTo(document);
				break;
			case 'DownloadHistory':
				downloadHistory(document);
				break;
			case 'EmailLender':
				sendDocument(document);
				break;
			case 'Delete':
				deleteDocument(document);
				break;
			case 'Link':
				linkDocument(document);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: DocumentActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
										{DocumentActionItemMap['Edit']}
									</MenuItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Move')}>
										{DocumentActionItemMap['Move']}
									</MenuItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'CopyTo')}>
										{DocumentActionItemMap['CopyTo']}
									</MenuItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'DownloadHistory')}>
										{DocumentActionItemMap['DownloadHistory']}
									</MenuItem>
									{isEmployee && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'EmailLender')}>
											{DocumentActionItemMap['EmailLender']}
										</MenuItem>
									)}
									{!isBanker && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Link')}>
											{DocumentActionItemMap['Link']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
										{DocumentActionItemMap['Delete']}
									</MenuItem>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
